import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useAPI } from "../../contexts/APIContext";
import { useAuth } from "../../contexts/AuthContext";
import { useProfile } from "../../contexts/ProfileContext";
import logger from "../../utils/logger";
import { courseKeyForLetter, selectLearnLetterProgress, D_STARTED_MARKER } from "../../utils/learnLetterProgress";
import { COUNTING_ACTIVITY_IDS, COUNTING_COURSE_KEY } from "../../data/countingLadderCatalog";
import { selectLearnCountingProgress } from "../../utils/learnCountingProgress";
import {
  WRITING_ACTIVITY_IDS_BY_LESSON,
  WRITING_TOWN_ID_SET,
  WRITING_TOWN_KEY,
  courseKeyForWritingLesson,
} from "../../data/writingTownCatalog";
import { selectLearnWritingProgress } from "../../utils/learnWritingProgress";

const MARKERS_KEY = "markers";
const STORAGE_PREFIX = "pixley_learn_progress_v1:";
const OUTBOX_PREFIX = "pixley_learn_outbox_v1:";

const LearnProgressContext = createContext(null);

function unionCourses(a = {}, b = {}) {
  const out = { ...a };
  Object.entries(b).forEach(([k, ids]) => {
    const prev = Array.isArray(out[k]) ? out[k] : [];
    out[k] = [...new Set([...prev, ...(Array.isArray(ids) ? ids : [])])].sort();
  });
  return out;
}

function readLocal(key) {
  try { return JSON.parse(localStorage.getItem(key) || "null"); } catch { return null; }
}
function writeLocal(key, value) {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* private mode */ }
}

/**
 * ONE source of truth for Learn progress across every Learn screen.
 *  - one GET per profile (not per screen), cancelled on profile switch
 *  - writes serialized through a queue; a stale response can never un-complete
 *    a game because responses are unioned, never replaced
 *  - localStorage mirror + outbox: offline completions survive reload and are
 *    replayed on reconnect (iOS parity: LearnProgressStore re-sends its record)
 *  - writes the `markers` the alphabet unlock rules depend on (letter.d.started)
 *  - exposes `error`/`offline` so pages can SHOW a state instead of a locked tree
 */
export function LearnProgressProvider({ children }) {
  const { apiClient } = useAPI();
  const { isAuthenticated } = useAuth();
  const { currentProfile } = useProfile();
  const profileId = currentProfile?.id || null;

  const [courses, setCourses] = useState({});
  const [updatedAt, setUpdatedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [offline, setOffline] = useState(typeof navigator !== "undefined" && navigator.onLine === false);
  const coursesRef = useRef(courses);
  coursesRef.current = courses;
  const queueRef = useRef(Promise.resolve());
  const genRef = useRef(0);

  const storageKey = profileId ? `${STORAGE_PREFIX}${profileId}` : null;
  const outboxKey = profileId ? `${OUTBOX_PREFIX}${profileId}` : null;

  const merge = useCallback((incoming, { persist = true } = {}) => {
    setCourses((prev) => {
      const next = unionCourses(prev, incoming);
      if (persist && storageKey) writeLocal(storageKey, { courses: next, updated_at: Date.now() });
      return next;
    });
  }, [storageKey]);

  const extract = (payload) => {
    const p = payload?.progress || payload?.data?.progress || payload || {};
    if (p.v != null && Number(p.v) > 1) logger.warn(`Learn progress schema v${p.v} is newer than this client (v1); merging what we understand.`);
    return { courses: p.courses && typeof p.courses === "object" ? p.courses : {}, updatedAt: p.updated_at || null };
  };

  // ── Load: local mirror first (instant), then server, then replay outbox ──
  const refresh = useCallback(async () => {
    if (!apiClient || !isAuthenticated) { setLoading(false); return; }
    if (!profileId) {
      // Signed in but the child profile hasn't loaded yet: stay "loading" so
      // lock checks don't run against empty progress and bounce the child out
      // of an unlocked lesson. Give up after a while rather than spin forever.
      setLoading(true);
      const waitGen = ++genRef.current;
      setTimeout(() => { if (genRef.current === waitGen) setLoading(false); }, 8000);
      return;
    }
    const gen = ++genRef.current;
    setLoading(true);
    setError(null);
    const local = readLocal(storageKey);
    if (local?.courses) setCourses(local.courses);
    try {
      // ProfileContext pins the header in its own effect, which runs after ours
      // (child effects fire first) — pin it here so the first load isn't a 400.
      if (apiClient.activeProfileId !== profileId) apiClient.setActiveProfileId(profileId);
      const response = await apiClient.getLearnProgress();
      if (gen !== genRef.current) return;
      const { courses: server, updatedAt: ua } = extract(response);
      setUpdatedAt(ua);
      // server ∪ local (local may hold offline completions not yet replayed)
      setCourses((prev) => {
        const next = unionCourses(server, local?.courses || {});
        if (storageKey) writeLocal(storageKey, { courses: next, updated_at: Date.now() });
        return unionCourses(prev, next);
      });
      const outbox = readLocal(outboxKey) || {};
      if (Object.keys(outbox).length) {
        try {
          const r = await apiClient.postLearnProgress(outbox);
          if (gen === genRef.current) { merge(extract(r).courses); writeLocal(outboxKey, {}); }
        } catch { /* stays in outbox */ }
      }
    } catch (err) {
      if (gen !== genRef.current) return;
      logger.error("Learn progress load failed:", err);
      setError(err);
    } finally {
      if (gen === genRef.current) setLoading(false);
    }
  }, [apiClient, profileId, isAuthenticated, storageKey, outboxKey, merge]);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    const on = () => { setOffline(false); refresh(); };
    const off = () => setOffline(true);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, [refresh]);

  // ── Serialized, union-only writes with an offline outbox ─────────────
  const write = useCallback((delta) => {
    merge(delta); // optimistic, persisted locally
    // No profile yet (still loading, or signed out): keep it local. Callers re-run
    // once the profile arrives because their `write` identity changes.
    if (!apiClient || !profileId) return Promise.resolve(false);
    const run = async () => {
      setSaving(true);
      try {
        if (profileId && apiClient.activeProfileId !== profileId) apiClient.setActiveProfileId(profileId);
        const r = await apiClient.postLearnProgress(delta);
        merge(extract(r).courses);
        setError(null);
        return true;
      } catch (err) {
        logger.warn("Learn progress write queued for retry:", err?.message || err);
        const outbox = readLocal(outboxKey) || {};
        writeLocal(outboxKey, unionCourses(outbox, delta));
        setError(err);
        return false;
      } finally {
        setSaving(false);
      }
    };
    queueRef.current = queueRef.current.then(run, run);
    return queueRef.current;
  }, [apiClient, merge, outboxKey, profileId]);

  const has = useCallback((key, id) => (coursesRef.current[key] || []).includes(id), []);

  const markGameComplete = useCallback(async (letter, gameId) => {
    if (!letter || !gameId) return false;
    const key = courseKeyForLetter(letter);
    if (has(key, gameId)) return true;
    await write({ [key]: [gameId] });
    return true;
  }, [has, write]);

  /** Alphabet unlock rules need this marker — iOS writes it, now web does too. */
  const markLetterStarted = useCallback(async (letter) => {
    const L = String(letter || "").toUpperCase();
    const marker = L === "D" ? D_STARTED_MARKER : `letter.${L.toLowerCase()}.started`;
    if (has(MARKERS_KEY, marker)) return true;
    await write({ [MARKERS_KEY]: [marker] });
    return true;
  }, [has, write]);

  const markCountingComplete = useCallback(async (activityId) => {
    if (!activityId || !COUNTING_ACTIVITY_IDS.has(activityId)) return false;
    if (has(COUNTING_COURSE_KEY, activityId)) return true;
    await write({ [COUNTING_COURSE_KEY]: [activityId] });
    return true;
  }, [has, write]);

  const markWritingLessonComplete = useCallback(async (lessonNumber, activityId) => {
    const allowed = WRITING_ACTIVITY_IDS_BY_LESSON[Number(lessonNumber)];
    if (!activityId || !allowed?.has(activityId)) return false;
    const key = courseKeyForWritingLesson(lessonNumber);
    if (has(key, activityId)) return true;
    await write({ [key]: [activityId] });
    return true;
  }, [has, write]);

  const markWritingTownComplete = useCallback(async (townId) => {
    if (!townId || !WRITING_TOWN_ID_SET.has(townId)) return false;
    if (has(WRITING_TOWN_KEY, townId)) return true;
    await write({ [WRITING_TOWN_KEY]: [townId] });
    return true;
  }, [has, write]);

  const snapshot = useMemo(() => selectLearnLetterProgress(courses), [courses]);
  const countingSnapshot = useMemo(() => selectLearnCountingProgress(courses), [courses]);
  const writingSnapshot = useMemo(() => selectLearnWritingProgress(courses), [courses]);

  const value = useMemo(() => ({
    ...snapshot, ...countingSnapshot, ...writingSnapshot,
    courses, updatedAt, loading, saving, error, offline,
    signedOut: !isAuthenticated,
    refresh, markGameComplete, markLetterStarted, markCountingComplete, markWritingLessonComplete, markWritingTownComplete,
  }), [snapshot, countingSnapshot, writingSnapshot, courses, updatedAt, loading, saving, error, offline, isAuthenticated,
      refresh, markGameComplete, markLetterStarted, markCountingComplete, markWritingLessonComplete, markWritingTownComplete]);

  return <LearnProgressContext.Provider value={value}>{children}</LearnProgressContext.Provider>;
}

export function useLearnProgress() {
  const ctx = useContext(LearnProgressContext);
  if (!ctx) throw new Error("useLearnProgress must be used inside <LearnProgressProvider>");
  return ctx;
}

export default useLearnProgress;
